import { selectUser } from "../../features/user-profile/user-profile.selectors";
import { GetShippingMethodMagentoRequest, MagentoBookstoreAddress, MagentoBookstoreAddressCustomAttributes, RmsBookstoreAddress, SelectedShippingMethod, SetShippingMethodMagentoRequest, TaxResponse } from "./shop-checkout.interface";
import { StoreState } from "../../store/store.interface";
import store from "../../store/store";
import { isK12StorePurchaseFlow } from "../../common/utils/util";
import { getCustomerId } from "../customer/customer";
import { getTaxInfo } from "./shop-checkout";
import { getCartResponse } from "../cart/shop-cart/store/shop-cart.selectors";
import { isAddressValid } from '../../common/utils/address-util';

const SAME_AS_BILLING = 1;
enum REGION_KEY {
    USOrCA = "region_code",
    INTL = "region"
}

export const addressRequest = (address: RmsBookstoreAddress, sameAsbilling?: number, customeAttributes?: MagentoBookstoreAddressCustomAttributes) => {
    const state = store.getState() as StoreState;

    let regionKey = REGION_KEY.USOrCA;
    const isAddressCountryIntl = address.country !== 'US' && address.country !== 'CA';
    if(isAddressCountryIntl) {
        regionKey = REGION_KEY.INTL
    }

    const street = [address.address1];
    if(address?.address2) {
        street.push(address?.address2);
    }

    const addressRequest: MagentoBookstoreAddress  = {
        [regionKey]: address.state,
        country_id: address.country,
        street: street,
        postcode: address.zipCode,
        city: address.city,
        firstname: address.firstName,
        lastname: address.lastName,
        customer_id: getCustomerId(state),
        email: selectUser(state)?.email,
        telephone: address.phoneNumber,
        same_as_billing: sameAsbilling,
        custom_attributes: {
            erp_site_id: address.partySiteId,
            rdi_type: address.rdiType?.[0],
            validation_flag: isAddressValid(address) ? '1' : '0',
            county: address.county,
            ...customeAttributes,
        }
    }
    return addressRequest;
}

export const freightCalculationMagentoRequest = (address: RmsBookstoreAddress) => {
    const freightCalculationRequest: GetShippingMethodMagentoRequest = {
        address: addressRequest(address, SAME_AS_BILLING)
    }
    return freightCalculationRequest;
}

export const paymentTaxMagentoRequest = (shippingAddress: RmsBookstoreAddress, billingAddress: RmsBookstoreAddress, shippingMethod?: SelectedShippingMethod) => {
    const freightCalculationRequest: SetShippingMethodMagentoRequest = {
        addressInformation: {
            shipping_address: addressRequest(shippingAddress),
            billing_address: addressRequest(billingAddress, undefined, _taxInfoRequest()),
            shipping_carrier_code: shippingMethod?.freightEstimateTypes.carrier,
            shipping_method_code: shippingMethod?.freightEstimateTypes.shippingMethodCode
        }
    }
    return freightCalculationRequest;
}

const _taxInfoRequest = () => {
    const taxInfo = getTaxInfo(store.getState() as StoreState);
    return {
        tax_id: taxInfo?.taxId,
        tax_name: taxInfo?.name,
        tax_postal_code: taxInfo?.postalCode,
        tax_type_of_name: taxInfo?.selectedPersonType?.id.toString(),
        tax_type_of_id: taxInfo?.selectedTaxRegime?.id.toString(),
        tax_reason: taxInfo?.selectedCfdi?.id,
        tax_type_of_document: taxInfo?.selectedDocumentType?.id.toString(),
        tax_municipality: taxInfo?.selectedCity?.id,
        tax_region: taxInfo?.selectedRegion?.id.toString(),
        tax_representation: taxInfo?.selectedRepresentation?.id,
    };
}

export const mapMagentoShippingMethodsResponse = (shippingResponse) => {
    const cart = getCartResponse(store.getState() as StoreState).data;
    const shippingMethods: SelectedShippingMethod = {
        ...shippingResponse,
        freightEstimateTypes: shippingResponse.filter(shipping => !!shipping.method_code).map(shipping => ({
            shippingMethodCode: shipping.method_code,
            shipMethodDescription: shipping.method_title,
            carrier: shipping.carrier_code,
            freightPrice: {	
                value: shipping.amount,
                currencyCode: cart?.currencyCode
            },
            handlingPrice: {
                value: 0,
                currencyCode: cart?.currencyCode
            },
        })),
    }
    return shippingMethods;
}

export const mapMagentoTaxResponse = (taxResponse) => {
    const tax: TaxResponse = {
        totalTax: {amount: getTotalTax(taxResponse)},
        digitalHandlingFees: {amount: getDigitalHandlingFees(taxResponse)}
    };
    return tax;
}

const getTaxSegment = (taxResponse) => taxResponse.total_segments.find(ts => ts.code === "tax");

const getTotalTax = (taxResponse) => {
    if (isK12StorePurchaseFlow()) {
        return taxResponse.tax_amount;
    }

    const taxSegment = getTaxSegment(taxResponse);
    return taxSegment?.extension_attributes?.tax ?? 0;
}

const getDigitalHandlingFees = (taxResponse) => {
    const taxSegment = getTaxSegment(taxResponse);
    return taxSegment.extension_attributes?.digital_handling_fees || 0;
}